import { memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Home } from 'lucide-react';
import type { Id } from '../../convex/_generated/dataModel';
import {
  WizardStepIndicatorDynamic,
  NEW_PROPERTY_STEP_LABELS,
  DRAFT_PROPERTY_STEP_LABELS,
} from '@/components/property/PropertyVideoWizardComponents';
import type { PropertyVideoWizardStep } from '@/@types/property';
import { PropertyVideoStepContent } from '@/pages/PropertyVideoStepContent';
import { usePropertyVideoWizard } from '@/hooks/usePropertyVideoWizard';

function PropertyVideoPage() {
  const [searchParams] = useSearchParams();
  const initialPropertyId = searchParams.get('propertyId') as Id<'propertyListings'> | null;

  const wizard = usePropertyVideoWizard(initialPropertyId);

  const stepLabels =
    wizard.wizardFlow === 'draft' ? DRAFT_PROPERTY_STEP_LABELS : NEW_PROPERTY_STEP_LABELS;
  const totalSteps = stepLabels.length;

  return (
    <div className="container mx-auto max-w-6xl px-4 py-8">
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <Home className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Property Video Generator</h1>
            <p className="mt-1 text-muted-foreground">
              Create professional property marketing videos with AI
            </p>
          </div>
        </div>
      </div>

      <WizardStepIndicatorDynamic
        currentStep={wizard.getDisplayStep(wizard.currentStep)}
        totalSteps={totalSteps}
        stepLabels={stepLabels}
        onStepClick={displayStep => wizard.handleStepClick(wizard.getInternalStep(displayStep))}
        finalStepClickable={!!wizard.propertyVideo?.finalVideoId}
      />

      <div className="flex justify-center">
        <PropertyVideoStepContent
          currentStep={wizard.currentStep}
          wizardFlow={wizard.wizardFlow}
          property={wizard.property}
          propertyId={wizard.propertyId}
          currentVideoId={wizard.currentVideoId}
          isScrapingInProgress={wizard.isScrapingInProgress}
          isScrapingFailed={wizard.isScrapingFailed}
          isScraping={wizard.isScraping}
          isAnalyzing={wizard.isAnalyzing}
          isGenerating={wizard.isGenerating}
          isReady={wizard.isReady}
          scrapingStatus={wizard.scrapingStatus}
          scrapingErrorMessage={wizard.scrapingErrorMessage}
          isCreatingVideo={wizard.isCreatingVideo}
          startError={wizard.startError}
          selectedVoiceId={wizard.selectedVoiceId}
          selectedDuration={wizard.selectedDuration}
          selectedAspectRatio={wizard.selectedAspectRatio}
          fullVideoGeneratorState={wizard.fullVideoGeneratorState}
          propertyVideo={wizard.propertyVideo}
          finalVideoUrl={wizard.finalVideoUrl}
          getDisplayStep={wizard.getDisplayStep}
          onUrlSubmit={wizard.handleUrlSubmit}
          onRetry={wizard.handleRetry}
          onDataReviewContinue={wizard.handleDataReviewContinue}
          onGoBack={wizard.handleGoBack}
          onNearbyPlacesContinue={wizard.handleNearbyPlacesContinue}
          onImageProcessingContinue={wizard.handleImageProcessingContinue}
          onImageProcessingBack={() => wizard.dispatchSetStep(3 as PropertyVideoWizardStep)}
          onClearError={() => wizard.setStartError(null)}
          onVoiceChange={wizard.setSelectedVoiceId}
          onDurationChange={wizard.setSelectedDuration}
          onAspectRatioChange={wizard.setSelectedAspectRatio}
          onGenerateFullVideo={wizard.handleGenerateFullVideo}
          onResetFullVideoGenerator={wizard.resetFullVideoGenerator}
          dispatchSetStep={wizard.dispatchSetStep}
        />
      </div>
    </div>
  );
}

export const PropertyVideoPageMemo = memo(PropertyVideoPage);
